import firmaEmpleador from "../../assets/firmaEmpleador.png";
import { PDF_FONT_FAMILY } from "../../utils/pdfFonts";
import { Document, Page, Text, View, Image, StyleSheet, pdf } from "@react-pdf/renderer";

// Recibo de sueldo PDF (layout tipo recibo del contador Merinell). Usa el MISMO motor
// (calcularLiquidacionChile): el caller pasa el objeto `liquidacion` ya calculado + rem/uf/utm,
// así el PDF coincide al peso con el modal Liquidación del mismo período. FASE Prod-2.

// ─── Datos de empresa — Merinell/Chile fijo (confirmados desde los recibos reales) ───
// TODO: parametrizar por marca (Wecast/Visiona/Perú) más adelante.
const EMPRESA = {
  nombre: "MERINELL SPA",
  rut: "76.857.932-6",
  direccion: "Guardia Vieja #202, Depto. #403, Macul, Santiago",
};

// Firma del empleador (Ignacio), fondo transparente, incrustada sobre la línea de firma.
const FIRMA_EMPLEADOR = firmaEmpleador;

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const clp = (n) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 9, fontFamily: PDF_FONT_FAMILY, color: "#1e293b" },
  empresa: { fontSize: 13, fontWeight: "bold" },
  empresaLinea: { fontSize: 8, color: "#475569" },
  titulo: { fontSize: 12, fontWeight: "bold", textAlign: "center", marginTop: 14, marginBottom: 12 },
  empBox: { borderWidth: 1, borderColor: "#cbd5e1", padding: 8, marginBottom: 12 },
  empRow: { flexDirection: "row", justifyContent: "space-between", marginBottom: 2 },
  seccionTit: { fontSize: 10, fontWeight: "bold", backgroundColor: "#e2e8f0", padding: 4, marginTop: 8 },
  fila: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: "#f1f5f9",
  },
  filaTotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
    backgroundColor: "#f8fafc",
    fontWeight: "bold",
  },
  liquidoBox: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 12,
    borderTopWidth: 1.5,
    borderTopColor: "#334155",
    paddingTop: 6,
  },
  liquidoLabel: { fontSize: 12, fontWeight: "bold" },
  footer: { marginTop: 10, fontSize: 8, color: "#64748b", textAlign: "center" },
  firmas: { flexDirection: "row", justifyContent: "space-between", marginTop: 40 },
  firmaCol: { width: "45%", alignItems: "center" },
  firmaImg: { width: 120, height: 48, objectFit: "contain" },
  firmaEspacio: { height: 48 },
  firmaLinea: { borderTopWidth: 1, borderTopColor: "#334155", width: "100%", paddingTop: 3, alignItems: "center" },
  firmaTexto: { fontSize: 8, textAlign: "center" },
});

const Fila = ({ label, valor, negativo }) => (
  <View style={styles.fila}>
    <Text>{label}</Text>
    <Text style={negativo ? { color: "#b91c1c" } : {}}>{negativo ? `- ${clp(valor)}` : clp(valor)}</Text>
  </View>
);

// liq = retorno de calcularLiquidacionChile + { rem, uf, utm }.
export const ReciboSueldoDocument = ({ empleado, liq, año, mes }) => {
  const rem = liq.rem || {};
  const saludLabel =
    rem.salud_tipo === "isapre"
      ? `Isapre (UF ${rem.salud_isapre_uf} × ${(liq.uf || 0).toFixed(2)}, piso 7%)`
      : "Fonasa (7%)";
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          <Text style={styles.empresa}>{EMPRESA.nombre}</Text>
          <Text style={styles.empresaLinea}>{EMPRESA.direccion}</Text>
          <Text style={styles.empresaLinea}>RUT {EMPRESA.rut}</Text>
        </View>

        <Text style={styles.titulo}>
          Liquidación de Sueldo de {MESES[mes - 1]} de {año}
        </Text>

        <View style={styles.empBox}>
          <View style={styles.empRow}>
            <Text>Nombre: {`${empleado.nombre || ""} ${empleado.apellido || ""}`.trim()}</Text>
            <Text>RUT: {empleado.rut || "—"}</Text>
          </View>
          <View style={styles.empRow}>
            <Text>Cargo: {empleado.cargo || "—"}</Text>
            <Text>Días no trabajados: 0</Text>
          </View>
        </View>

        <Text style={styles.seccionTit}>HABERES</Text>
        <Fila label="Sueldo base" valor={liq.sueldoBase} />
        {liq.bonosFijo > 0 && <Fila label="Bono fijo" valor={liq.bonosFijo} />}
        {liq.bonosVariable > 0 && <Fila label="Bono variable" valor={liq.bonosVariable} />}
        {liq.haberesImponibles > 0 && <Fila label="Comisión / haberes del mes" valor={liq.haberesImponibles} />}
        <Fila label="Gratificación" valor={liq.gratificacion} />
        <View style={styles.filaTotal}>
          <Text>Total imponible</Text>
          <Text>{clp(liq.totalImponible)}</Text>
        </View>

        <Text style={styles.seccionTit}>DESCUENTOS</Text>
        <Fila
          label={`AFP ${rem.afp_nombre || ""} (cap. ${rem.afp_cap_individual_pct ?? 10}% + com. ${rem.afp_comision_pct || 0}%)`}
          valor={liq.totalAfp}
          negativo
        />
        <Fila label={saludLabel} valor={liq.totalSalud} negativo />
        <Fila label="Seguro de cesantía (0,6%)" valor={liq.segCesantia} negativo />
        <Fila label={`Impuesto único (base ${clp(liq.baseImpuesto)})`} valor={liq.impuestoUnico} negativo />
        <View style={styles.filaTotal}>
          <Text>Total descuentos legales</Text>
          <Text style={{ color: "#b91c1c" }}>- {clp(liq.totalDescuentosLegales)}</Text>
        </View>

        <View style={styles.liquidoBox}>
          <Text style={styles.liquidoLabel}>LÍQUIDO A PAGAR</Text>
          <Text style={styles.liquidoLabel}>{clp(liq.liquido)}</Text>
        </View>

        <Text style={styles.footer}>
          UF del mes: {(liq.uf || 0).toLocaleString("es-CL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {"     "}·{"     "}UTM del mes: {clp(liq.utm)}
        </Text>

        <View style={styles.firmas}>
          <View style={styles.firmaCol}>
            {FIRMA_EMPLEADOR ? (
              <Image src={FIRMA_EMPLEADOR} style={styles.firmaImg} />
            ) : (
              <View style={styles.firmaEspacio} />
            )}
            <View style={styles.firmaLinea}>
              <Text style={styles.firmaTexto}>{EMPRESA.nombre}</Text>
            </View>
          </View>
          <View style={styles.firmaCol}>
            <View style={styles.firmaEspacio} />
            <View style={styles.firmaLinea}>
              <Text style={styles.firmaTexto}>Recibí conforme</Text>
              <Text style={styles.firmaTexto}>{`${empleado.nombre || ""} ${empleado.apellido || ""}`.trim()}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Genera el PDF y dispara la descarga. `liquidacion` = calcularLiquidacionChile(...) + { rem, uf, utm }.
export const generarReciboPDF = async (empleado, liquidacion, año, mes) => {
  const doc = <ReciboSueldoDocument empleado={empleado} liq={liquidacion} año={año} mes={mes} />;
  const blob = await pdf(doc).toBlob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const nombreArch = `Recibo_${(empleado.apellido || "").replace(/\s+/g, "")}_${año}-${String(mes).padStart(2, "0")}.pdf`;
  link.download = nombreArch;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
